import * as APIRoutes from '@mm/api-routes';
import { ObjectUtils, timeout } from '@mm/utils';
import { cloneDeep } from 'lodash';
import { Dispatch } from 'redux';
import WidgetController, {
  SplitPageUsingGoToWidgetParams,
} from '../controllers/WidgetController';
import commonHandler from '../errorHandlers/common-handler';
import {
  failedValidationHandler,
  update as updateWidgetHandler,
} from '../errorHandlers/widget-handler';
import WidgetModel from '../model/WidgetModel';
import { StoreState } from '../store-interfaces';
import widgetChangeManager from '../syncEngine/services/changeManagers/WidgetChangeManager';
import { getActionHookBuilder } from './action-hook-builder';
import chatbotCRUDActions from './ChatbotCRUDActions';
import pageCRUDActions from './PageCRUDActions';
import CrudActionContainer from './CrudActionContainer';
import { setWidgetToFocus } from './viewActions';

export type WidgetCreateParams = {
  buttons?: any[];
  config: {
    body?: string;
    [key: string]: any;
  };
  pageId: number;
  sortOrder: number;
  type: string;
  [key: string]: any;
};

class WidgetCRUDActions extends CrudActionContainer {
  updateButtonLocal: string;
  deleteButtonLocal: string;
  updateEntryLocal: string;
  deleteEntryLocal: string;
  updateEntryButtonLocal: string;
  deleteEntryButtonLocal: string;
  setBusyWidgetAction: string;
  setUnBusyWidgetAction: string;
  retrieveAllByPageIdAction: string;
  unsetEntitiesAction: string;
  setSelectedPathId: string;
  dropActivePathId: string;
  batchDeleteLocalAction: string;

  constructor() {
    super('WIDGET');
    this.updateButtonLocal = this.makeActionKey('BUTTON_UPDATE_LOCAL');
    this.deleteButtonLocal = this.makeActionKey('BUTTON_DELETE_LOCAL');
    this.updateEntryLocal = this.makeActionKey('ENTRY_UPDATE_LOCAL');
    this.deleteEntryLocal = this.makeActionKey('ENTRY_DELETE_LOCAL');
    this.updateEntryButtonLocal = this.makeActionKey(
      'ENTRY_BUTTON_UPDATE_LOCAL',
    );
    this.deleteEntryButtonLocal = this.makeActionKey(
      'ENTRY_BUTTON_DELETE_LOCAL',
    );
    this.setBusyWidgetAction = this.makeActionKey('SET_BUSY_WIDGET');
    this.setUnBusyWidgetAction = this.makeActionKey('UNSET_BUSY_WIDGET');
    this.retrieveAllByPageIdAction = this.makeActionKey(
      'RETRIEVE_ALL_BY_PAGE_ID',
    );
    this.unsetEntitiesAction = this.makeActionKey('UNSET_ENTITIES');
    this.dropActivePathId = this.makeActionKey('DROP_SELECTED_PATH_ID');
    this.batchDeleteLocalAction = this.makeActionKey('BATCH_DELETE_LOCAL');
  }

  dropActivePathIdAction(id?: number) {
    return (dispatch: Dispatch<StoreState>) => {
      dispatch({ type: this.dropActivePathId, selectedPathId: id });
    };
  }

  create(widget: WidgetCreateParams) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        const response = await WidgetController.post(APIRoutes.WIDGETS, widget);
        dispatch(this.retrieved(response));
        dispatch(this.createSuccess(response));
        dispatch(setWidgetToFocus(response.id));
        return response;
      } catch (err) {
        dispatch(this.createFail<WidgetCreateParams>(widget));
        commonHandler(dispatch)(err);
        throw err;
      }
    };
  }

  retrieveAll(pageId: number, skipBusy = false) {
    return async (dispatch: Dispatch<StoreState>) => {
      if (!skipBusy) {
        dispatch(this.busy(this.retrieveAllAction));
      }

      try {
        const response = await WidgetController.getAll(pageId);
        dispatch(this.retrieveAllByPageIdActionCreator(pageId, response));

        return response;
      } catch (err) {
        commonHandler(dispatch)(err);
        throw err;
      }
    };
  }

  show(entityId: number, includeStructure?: boolean) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        const response = await WidgetController.getWidget(
          entityId,
          includeStructure,
        );
        if (!includeStructure) {
          dispatch(this.updateLocal(response));
        }
        dispatch(this.setUnBusyWidget(entityId));
        return response;
      } catch (e) {
        dispatch(this.setUnBusyWidget(entityId));
        commonHandler(dispatch)(e);
      }
    };
  }

  batchUpdate(
    dialogueId: number,
    widgets: WidgetModel[],
    options: {
      sortOrdersOnly?: boolean;
      normalizeOrdering?: boolean;
      syncButtons?: boolean;
    } = {},
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        dispatch(this.retrieveAllByPageIdActionCreator(dialogueId, widgets));
        const response = await WidgetController.patchWidget(
          dialogueId,
          widgets,
          options,
        );

        await dispatch(
          this.retrieveAllByPageIdActionCreator(dialogueId, response),
        );
        return response;
      } catch (e) {
        if (window.MMApp?.throwErrors) {
          throw e;
        }
        await commonHandler(dispatch)(e);
      }
    };
  }

  update(updatedWidget, withPatch = true) {
    return async (dispatch: Dispatch<StoreState>, getState) => {
      const {
        widget: { entities: widgets },
      } = getState();
      const widgetInstance = widgets.find(
        widget => widget.id === updatedWidget.id,
      );
      dispatch(
        chatbotCRUDActions.updateSetupStepsRemainingByPageIdUpdated(
          updatedWidget.pageId,
        ),
      );

      dispatch(this.busy(this.updateAction));
      dispatch(this.updateLocal(updatedWidget));
      dispatch(this.setBusyWidget(updatedWidget.id));

      const copy = cloneDeep(updatedWidget);
      ObjectUtils.removeProperty('file', copy);
      ObjectUtils.removeProperty('validated', copy);

      if (withPatch) {
        try {
          const response = await widgetChangeManager.update(
            widgetInstance,
            copy,
          );
          if (response.errors) {
            const updated = cloneDeep(widgetInstance);

            const newErrors = response.errors.map(error => ({
              field: '#/config/name',
              rule: {
                type: 'Unique',
                arguments: error.split(':')[1],
              },
            }));

            updated.validationMessages = [
              ...updated.validationMessages,
              ...newErrors,
            ];
            updated.validated = {
              ...(updated.validated || {}),
              facebook: false,
            };
            dispatch(this.updateLocal(updated));
            dispatch(this.setUnBusyWidget(updated.id));
            return response;
          }
          dispatch(this.updateLocal(response));
          dispatch(this.setUnBusyWidget(response.id));
          return response;
        } catch (e) {
          await updateWidgetHandler()(e);
        }
      }

      dispatch(this.updateLocal(widgetInstance));
      dispatch(this.setUnBusyWidget(widgetInstance.id));

      return widgetChangeManager
        .update(widgetInstance, copy)
        .catch(failedValidationHandler(dispatch, widgetInstance))
        .catch(commonHandler(dispatch));
    };
  }

  splitPageUsingGoToWidget(
    params: SplitPageUsingGoToWidgetParams & { botId: number },
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      const { botId, ...rest } = params;
      try {
        const res = await WidgetController.splitPageUsingGoToWidget(rest);
        await dispatch(pageCRUDActions.retriveDropdownPages(botId, true));

        if (res?.config) {
          await Promise.all([
            dispatch(this.retrieveAll(res.pageId)),
            dispatch(this.retrieveAll(res.config.pageId)),
            dispatch(this.retrieveAll(res.config.oppositePageId)),
          ]);
        }
        return res;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  // Overrides standard updateLocal to prevent remapping to BE data model
  updateLocal(entity) {
    return {
      type: this.updateLocalAction,
      entity,
    };
  }

  setBusyWidget(widgetId: number) {
    return {
      type: this.setBusyWidgetAction,
      widgetId,
    };
  }

  setUnBusyWidget(widgetId: number) {
    return {
      type: this.setUnBusyWidgetAction,
      widgetId,
    };
  }

  delete(widget, skipBusy = false) {
    return (dispatch: Dispatch<StoreState>) => {
      if (!skipBusy) {
        dispatch(this.busy(this.deleteAction));
      }
      dispatch(this.deleteLocal(widget));

      return widgetChangeManager.delete(widget).catch(commonHandler(dispatch));
    };
  }

  batchDelete(widgetIds: number[]) {
    return async (dispatch: Dispatch<StoreState>) => {
      const result = await WidgetController.batchDelete(widgetIds).catch(
        commonHandler(dispatch),
      );
      dispatch(this.batchDeleteLocal(widgetIds));
      return result;
    };
  }

  clone(widgetId: number, dialogId?: number, sortOrder?: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      try {
        const response = await WidgetController.post(
          `${APIRoutes.WIDGETS}/${widgetId}/clone`,
          {
            pageId: dialogId,
            sortOrder,
          },
        );
        dispatch(this.retrieved(response));
        dispatch(this.createSuccess(response));
        dispatch(setWidgetToFocus(response.id));
        return response;
      } catch (err) {
        commonHandler(dispatch)(err);
        throw err;
      }
    };
  }

  retrieveAllByPageIdActionCreator = (
    pageId: number,
    widgets: WidgetModel[],
  ) => ({
    type: this.retrieveAllByPageIdAction,
    pageId,
    widgets,
  });

  unsetEntities = () => ({
    type: this.unsetEntitiesAction,
  });
  batchDeleteLocal = (widgetIds: number[]) => ({
    type: this.batchDeleteLocalAction,
    widgetIds,
  });
}

const widgetCRUDActions = new WidgetCRUDActions();

export default widgetCRUDActions;

export const useWidgetCRUDActions = getActionHookBuilder(widgetCRUDActions, [
  'create',
  'retrieveAll',
  'show',
  'batchUpdate',
  'clone',
  'update',
  'delete',
  'unsetEntities',
  'dropActivePathIdAction',
  'batchDelete',
  'updateLocal',
  'splitPageUsingGoToWidget',
]);
